import { AxiosResponse } from 'axios';
import { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';

import { CompanyI } from '~src/interfaces/company.interface';
import { defaultBaseApi } from '~src/services/ApiController';
import logger from '~src/services/logger/LlsLogger';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { initUserLogin, multiLoginLoading, multiLoginNotLoading } from '~src/store/session/actions';
import { StoreI } from '~src/rootReducer';
import { ThunkAnyAction } from '~src/services/apiMiddleware';

import { AccountsHeader, AccountsLogin } from './accounts.sc';

interface StateProps {
  tokens: string[];
  hasMultipleAccounts: boolean;
}

interface DispatchProps {
  initUserLogin(token: string): Promise<[ThunkAnyAction, AnyAction]>;
  multiLoginLoading(): AnyAction;
  multiLoginNotLoading(): AnyAction;
}

type Props = StateProps & DispatchProps;

interface CompanyToken {
  name: string;
  token: string;
}

function isFulfilled<T>(s: PromiseSettledResult<T>): s is PromiseFulfilledResult<T> {
  return s.status === 'fulfilled';
}

const Accounts: FC<Props> = (props: Props) => {

  const [accounts, setAccounts] = useState<CompanyToken[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingCount, setLoadingCount] = useState<[number, number]>([0, 0]);

  useEffect(() => {
    if (props.hasMultipleAccounts) {
      setIsLoading(true);
      setLoadingCount([0, props.tokens.length]);
      Promise.allSettled(props.tokens.map((token) => fetchAccount(token))).then((settles) => {
        setAccounts(settles.filter(isFulfilled).map((s: PromiseFulfilledResult<CompanyToken>) => s.value))
      }).catch((error) => {
        logger.info('Error on fetching Account', {
          error,
          info: {
            componentStack: 'Accounts.tsx',
          },
        });
        throw error;
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [props.tokens]);

  const onClick = (token: string) => {
    props.multiLoginLoading();
    props.initUserLogin(token).finally(() => {
      props.multiLoginNotLoading();
    });
  };

  const fetchAccount = (token: string): Promise<CompanyToken> => {
    return new Promise((resolve, reject) => {
      defaultBaseApi.getCompany({headers: {Authorization: `Bearer ${token}`}}).then((response: AxiosResponse<CompanyI>) => {
        setLoadingCount(([counter, total]) => [counter + 1, total]);
        resolve({ token, name: response.data.name });
      }).catch((error) => reject(error));
    });
  };

  return (
    <section>
      <div>
        {isLoading && <LoadingSpinner alignCls="center-middle" marginTop={"5em"} />}
        {isLoading && <div>{loadingCount[0]}/{loadingCount[1]}</div>}
        {accounts
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((account) => (
            <AccountsLogin key={account.token} onClick={onClick.bind(null, account.token)}>
              <AccountsHeader>{account.name}</AccountsHeader>
            </AccountsLogin>
          ))}
      </div>
    </section>
  );
}

const mapState2Props = ({
  user: {
    session: { accounts },
  },
}: StoreI): StateProps => ({
  hasMultipleAccounts: accounts.hasMultipleAccounts,
  tokens: accounts.tokens,
});

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  initUserLogin: (token) => dispatch(initUserLogin(token)),
  multiLoginLoading: () => dispatch(multiLoginLoading()),
  multiLoginNotLoading: () => dispatch(multiLoginNotLoading()),
});

const AccountsC = connect(mapState2Props, mapDispatch2Props)(Accounts);
export default AccountsC;
